// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngCarousel from "@rpsc/App/Components/XngCarousel";
import LazyPicture from "@rpsc/MobiusOverrides/LazyImage/LazyPicture";
import { XNGAGE_CUSTOM_WIDGET } from "@rpsc/XDL/constants";
import { isTouchDevice } from "@rpsc/XDL/Utilities";
import parse from "html-react-parser";
import React, { useMemo } from "react";

const enum fields {
    showArrows = "showArrows",
    animationSpeed = "animationSpeed",
    slides = "slides",
    desktopImage = "desktopImage",
    tabletImage = "tabletImage",
    mobileImage = "mobileImage",
    altText = "altText",
    openInNewWindow = "openInNewWindow",
    slidesAutoPlay = "slidesAutoPlay",
    displayImageWithFullHeight = "displayImageWithFullHeight",
    animationType = "animationType",
    timerSpeed = "timerSpeed",
    showBullets = "showBullets",
    showNumbers = "showNumbers",
}

interface SlideModel {
    fields: {
        slideTitle: string;
        image: string;
        imageOverlay: string;
        backgroundColor: string;
        applyBgColorForTabletOnly: boolean;
        heading: string;
        subheading: string;
        buttonLabel: string;
        buttonLink: LinkFieldValue;
        buttonVariant: "primary" | "secondary" | "tertiary";
        contentPadding: number;
        centerTextVertically: boolean;
        customCSS: string;
        [fields.desktopImage]: string;
        [fields.tabletImage]: string;
        [fields.mobileImage]: string;
        [fields.openInNewWindow]: boolean;
        [fields.displayImageWithFullHeight]: boolean;
        [fields.altText]: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showArrows]: boolean;
        [fields.showBullets]: boolean;
        [fields.showNumbers]: boolean;
        [fields.animationSpeed]: number;
        [fields.timerSpeed]: number;
        [fields.slides]: SlideModel[];
        [fields.slidesAutoPlay]: boolean;
        [fields.animationType]: "fade" | "slide";
        autoHeight: boolean;
    };
}
export interface XBannerCarouselStyles {
    wrapper?: InjectableCss;
    slideshowWrapper?: InjectableCss;
    slideContentWrapper?: InjectableCss;
    slideOverlayWrapper?: InjectableCss;
    slideCenteringWrapperStyles?: InjectableCss;
    headingText?: TypographyPresentationProps;
    subheadingText?: TypographyPresentationProps;
    slideButton?: ButtonPresentationProps;
    pictureWrapper?: InjectableCss;
    zoneContainer?: InjectableCss;
}

export const xBannerCarouselStyles: XBannerCarouselStyles = {};

const styles = xBannerCarouselStyles;

export const XBannerCarousel = ({ fields, id }: OwnProps) => {
    const { isInShell } = useShellContext();
    const history = useHistory();
    const buttonLinks = useGetLinks(fields.slides, o => o.fields.buttonLink);

    const contentsArray = useMemo(
        () =>
            fields.slides.map(slide => ({
                heading: parse(slide.fields.heading, parserOptions),
                subHeading: parse(slide.fields.subheading, parserOptions),
            })),
        [fields.slides],
    );

    const onClick = (url: string | undefined, openInNewWindow: boolean) => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }
            openInNewWindow ? window.open(url) : (window.location.href = url);
        } else {
            openInNewWindow ? window.open(url) : history.push(url);
        }
    };

    if (!fields.slides || fields.slides.length === 0) {
        return null;
    }

    return (
        <StyledWrapper {...styles.slideshowWrapper}>
            <XngCarousel
                navigation={fields.showArrows}
                navigationOutside={false}
                draggable={isTouchDevice()}
                scrollbar={false}
                slidesPerView={1}
                rewind={true}
                speed={fields.animationSpeed}
                effect={fields.animationType}
                autoplay={
                    fields.slidesAutoPlay && {
                        delay: fields.timerSpeed,
                    }
                }
                autoHeight={fields.autoHeight}
                pagination={
                    (fields.showBullets || fields.showNumbers) &&
                    fields.slides.length > 1 && {
                        clickable: true,
                        el: ".swiper-pagination",
                        renderBullet: (index: number, className: string) =>
                            `<span class="${className} ${fields.showNumbers ? "dots-with-numbers" : ""}">
                                ${fields.showNumbers ? index + 1 : ""}
                            </span>`,
                    }
                }
            >
                {fields.slides.map((slide, index) => {
                    const buttonLink = buttonLinks[index];
                    const buttonVariant = slide.fields.buttonVariant;
                    const { heading = "", subHeading = "" } = contentsArray[index] || {};

                    return (
                        <StyledWrapper
                            {...styles.slideContentWrapper}
                            key={`slide-${index}`}
                            {...{
                                backgroundColor: slide.fields.backgroundColor,
                                tabletOnly: slide.fields.applyBgColorForTabletOnly,
                            }}
                        >
                            <StyledWrapper
                                {...styles.pictureWrapper}
                                {...{ displayImageWithFullHeight: slide.fields.displayImageWithFullHeight }}
                            >
                                <LazyPicture
                                    largeImageSrc={slide.fields.desktopImage || slide.fields.tabletImage}
                                    mediumImageSrc={slide.fields.tabletImage}
                                    smallImageSrc={slide.fields.mobileImage || slide.fields.tabletImage}
                                    altText={slide.fields.altText || translate("Slideshow-image")}
                                />
                            </StyledWrapper>

                            {(slide.fields.heading || slide.fields.subheading) && (
                                <StyledWrapper {...styles.wrapper}>
                                    <StyledWrapper
                                        {...styles.slideOverlayWrapper}
                                        {...{
                                            backgroundColor: slide.fields.imageOverlay,
                                        }}
                                    >
                                        <StyledWrapper {...styles.slideCenteringWrapperStyles}>
                                            {slide.fields.heading && (
                                                <Typography {...styles.headingText}>{heading}</Typography>
                                            )}
                                            {slide.fields.subheading && (
                                                <Typography {...styles.subheadingText}>{subHeading}</Typography>
                                            )}
                                            {(slide.fields.buttonLabel || slide.fields.buttonLink.value) && (
                                                <Button
                                                    {...styles.slideButton}
                                                    variant={buttonVariant}
                                                    onClick={() =>
                                                        onClick(buttonLink?.url, slide.fields.openInNewWindow)
                                                    }
                                                    aria-label={
                                                        slide.fields.buttonLabel || buttonLink?.title || buttonLink?.url
                                                    }
                                                >
                                                    {slide.fields.buttonLabel || buttonLink?.title || buttonLink?.url}
                                                </Button>
                                            )}
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </StyledWrapper>
                            )}

                            <StyledWrapper {...styles.zoneContainer}>
                                <Zone contentId={id} zoneName={`Content${index}`} />
                            </StyledWrapper>
                        </StyledWrapper>
                    );
                })}
            </XngCarousel>
        </StyledWrapper>
    );
};

const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: XBannerCarousel,
    definition: {
        group: XNGAGE_CUSTOM_WIDGET,
        icon: "presentation-screen",
        fieldDefinitions: [
            {
                name: fields.showArrows,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.showBullets,
                displayName: "Show bullets",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.showNumbers,
                displayName: "Show numbers",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.animationSpeed,
                editorTemplate: "IntegerField",
                defaultValue: 500,
                fieldType: "General",
                tab: settingsTab,
                min: 1,
            },
            {
                name: fields.slidesAutoPlay,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.timerSpeed,
                editorTemplate: "IntegerField",
                defaultValue: 10000,
                fieldType: "General",
                tab: settingsTab,
                min: 1,
            },
            {
                name: fields.animationType,
                displayName: "Animation Type",
                editorTemplate: "DropDownField",
                fieldType: "General",
                defaultValue: "slide",
                options: [
                    { displayName: "Slide", value: "slide" },
                    { displayName: "Fade", value: "fade" },
                ],
                tab: settingsTab,
                isRequired: true,
            },

            {
                name: fields.slides,
                editorTemplate: "ListField",
                tab: contentTab,
                getDisplay: ({ fields: { slideTitle } }) => slideTitle || "Slide",
                defaultValue: [],
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: "slideTitle",
                        editorTemplate: "TextField",
                        defaultValue: "Slide",
                    },
                    {
                        name: fields.desktopImage,
                        displayName: "Desktop Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: fields.tabletImage,
                        displayName: "Tablet Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: fields.mobileImage,
                        displayName: "Mobile Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: fields.altText,
                        displayName: "Alternative image text",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "displayImageWithFullHeight",
                        displayName: "Display Image With Full Height",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "imageOverlay",
                        displayName: "Tablet Image Color Overlay",
                        editorTemplate: "ColorPickerField",
                        defaultValue: "",
                    },
                    {
                        name: "backgroundColor",
                        editorTemplate: "ColorPickerField",
                        displayName: "Background Color",
                        defaultValue: "black",
                    },
                    {
                        name: "applyBgColorForTabletOnly",
                        displayName: "Apply Bg Color for Tablet & Mobile",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "autoHeight",
                        displayName: "Auto Slide Height",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "heading",
                        editorTemplate: "RichTextField",
                        displayName: "Heading",
                        defaultValue: "",
                        extendedConfig: { height: 100 },
                        expandedToolbarButtons: {
                            moreMisc: {},
                            code: {},
                        },
                    },
                    {
                        name: "subheading",
                        editorTemplate: "RichTextField",
                        displayName: "Subheading",
                        defaultValue: "",
                        extendedConfig: { height: 170 },
                        expandedToolbarButtons: {
                            moreMisc: {},
                            code: {},
                        },
                    },
                    {
                        name: "buttonLabel",
                        editorTemplate: "TextField",
                        displayName: "Button Label",
                        defaultValue: "",
                    },
                    {
                        name: "buttonLink",
                        displayName: "Button Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                    {
                        name: "buttonVariant",
                        displayName: "Button Variant",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Primary", value: "primary" },
                            { displayName: "Secondary", value: "secondary" },
                            { displayName: "tertiary", value: "tertiary" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "primary",
                    },
                    {
                        name: "openInNewWindow",
                        displayName: "Open in New Window",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
